import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
  getDaysInMonth,
  startOfWeek,
} from 'date-fns';

import { dateStringToDate, dateToDateString } from '@/lib/date-values';
import type { JobCreateFormValues } from '@/lib/jobs/jobFormDto';

type RecurringAvailabilityValues = Pick<
  JobCreateFormValues,
  | 'recurFrequency'
  | 'recurStartDate'
  | 'recurEndType'
  | 'recurEndOnDate'
  | 'recurInterval'
  | 'recurIntervalUnit'
  | 'recurWeekDays'
  | 'recurCustomDays'
  | 'recurMonthMode'
  | 'recurMonthDay'
  | 'recurMonthLastDay'
  | 'recurMonthDayOfWeek'
  | 'recurMonthDayOfWeekOccurrence'
  | 'recurSeasonStart'
  | 'recurSeasonEnd'
  | 'recurVisitPatterns'
>;

function recurringDatesPerPreviewCycle(values: RecurringAvailabilityValues): number {
  if (values.recurVisitPatterns.length > 0) {
    return values.recurVisitPatterns.length;
  }
  const weeklyDays =
    values.recurFrequency === 'WEEKLY' || values.recurFrequency === 'BIWEEKLY'
      ? values.recurWeekDays
      : values.recurFrequency === 'CUSTOM' && values.recurIntervalUnit === 'WEEKS'
        ? values.recurCustomDays
        : [];
  return Math.max(1, new Set(weeklyDays).size);
}

/**
 * Picks one resolved occurrence date for each recurrence day in the next cycle.
 * A single occurrence may move because of a holiday/service blackout, but that
 * moved date must not replace the other weekdays selected for the series.
 */
export function recurringAvailabilityPreviewDates(
  values: RecurringAvailabilityValues,
  occurrences: Array<string | { date: string; patternId?: string | null }>,
): string[] {
  const targetCount = recurringDatesPerPreviewCycle(values);
  const selected: string[] = [];
  const seenDates = new Set<string>();
  const seenPatterns = new Set<string>();
  const hasPatternIdentities = occurrences.some(
    (occurrence) => typeof occurrence !== 'string' && Boolean(occurrence.patternId),
  );

  for (const occurrence of occurrences) {
    const date = typeof occurrence === 'string' ? occurrence : occurrence.date;
    const patternId = typeof occurrence === 'string' ? null : occurrence.patternId;
    if (!date) continue;
    if (hasPatternIdentities && patternId) {
      if (seenPatterns.has(patternId)) continue;
      seenPatterns.add(patternId);
    }
    if (!seenDates.has(date)) {
      seenDates.add(date);
      selected.push(date);
    }
    const representedCount = hasPatternIdentities ? seenPatterns.size : selected.length;
    if (representedCount >= targetCount) break;
  }

  return selected;
}

function positiveInterval(value: string): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function isWithinSeason(date: Date, seasonStart?: string, seasonEnd?: string): boolean {
  if (!seasonStart || !seasonEnd) return true;
  const monthDay = dateToDateString(date).slice(5);
  return seasonStart <= seasonEnd
    ? monthDay >= seasonStart && monthDay <= seasonEnd
    : monthDay >= seasonStart || monthDay <= seasonEnd;
}

function matchesWeekdayPosition(date: Date, weekday: number, position: number): boolean {
  if (date.getDay() !== weekday) return false;
  if (position === -1) return date.getDate() + 7 > getDaysInMonth(date);
  return Math.floor((date.getDate() - 1) / 7) + 1 === position;
}

function matchesMonthlySelector(date: Date, start: Date, values: RecurringAvailabilityValues) {
  if (values.recurMonthMode === 'dayOfWeek' && values.recurMonthDayOfWeek != null) {
    return matchesWeekdayPosition(
      date,
      values.recurMonthDayOfWeek,
      values.recurMonthDayOfWeekOccurrence,
    );
  }
  const requestedDay = values.recurMonthLastDay
    ? getDaysInMonth(date)
    : (values.recurMonthDay ?? start.getDate());
  return date.getDate() === Math.min(requestedDay, getDaysInMonth(date));
}

function matchesVisitPattern(date: Date, values: RecurringAvailabilityValues): boolean {
  if (values.recurVisitPatterns.length === 0) return false;
  return values.recurVisitPatterns.some((pattern) => {
    if (pattern.kind === 'DAILY_TIME') return true;
    if (pattern.kind === 'WEEKDAY') return date.getDay() === pattern.dayOfWeek;
    if (pattern.kind === 'MONTH_DAY') {
      const requestedDay = pattern.dayOfMonth ?? 1;
      return date.getDate() === Math.min(requestedDay, getDaysInMonth(date));
    }
    if (pattern.kind === 'MONTH_WEEKDAY') {
      return matchesWeekdayPosition(date, pattern.dayOfWeek ?? 1, pattern.weekOfMonth ?? 1);
    }
    if (pattern.kind === 'YEAR_DATE') {
      return (
        date.getMonth() + 1 === (pattern.month ?? 1) &&
        date.getDate() === Math.min(pattern.dayOfMonth ?? 1, getDaysInMonth(date))
      );
    }
    return false;
  });
}

function matchesRecurrence(date: Date, start: Date, values: RecurringAvailabilityValues): boolean {
  const configuredInterval = positiveInterval(values.recurInterval);
  const frequency = values.recurFrequency;

  if (frequency === 'DAILY' || (frequency === 'CUSTOM' && values.recurIntervalUnit === 'DAYS')) {
    return (
      differenceInCalendarDays(date, start) % configuredInterval === 0 &&
      (values.recurVisitPatterns.length === 0 || matchesVisitPattern(date, values))
    );
  }

  if (
    frequency === 'WEEKLY' ||
    frequency === 'BIWEEKLY' ||
    (frequency === 'CUSTOM' && values.recurIntervalUnit === 'WEEKS')
  ) {
    const interval = frequency === 'BIWEEKLY' ? 2 : frequency === 'CUSTOM' ? configuredInterval : 1;
    const anchorWeek = startOfWeek(start, { weekStartsOn: 1 });
    const candidateWeek = startOfWeek(date, { weekStartsOn: 1 });
    const weekDays = frequency === 'CUSTOM' ? values.recurCustomDays : values.recurWeekDays;
    const patternDays = values.recurVisitPatterns
      .filter((pattern) => pattern.kind === 'WEEKDAY' && pattern.dayOfWeek != null)
      .map((pattern) => pattern.dayOfWeek!);
    const selectedDays =
      patternDays.length > 0 ? patternDays : weekDays.length > 0 ? weekDays : [start.getDay()];
    return (
      differenceInCalendarWeeks(candidateWeek, anchorWeek, {
        weekStartsOn: 1,
      }) %
        interval ===
        0 && selectedDays.includes(date.getDay())
    );
  }

  const yearly =
    frequency === 'ANNUAL' || (frequency === 'CUSTOM' && values.recurIntervalUnit === 'YEARS');
  if (yearly) {
    const interval = frequency === 'CUSTOM' ? configuredInterval : 1;
    if (differenceInCalendarYears(date, start) % interval !== 0) {
      return false;
    }
    if (values.recurVisitPatterns.some((pattern) => pattern.kind === 'YEAR_DATE')) {
      return matchesVisitPattern(date, values);
    }
    if (date.getMonth() !== start.getMonth()) return false;
    if (frequency === 'CUSTOM' && values.recurCustomDays.length > 0) {
      return values.recurCustomDays.includes(date.getDay());
    }
    return date.getDate() === Math.min(start.getDate(), getDaysInMonth(date));
  }

  const interval =
    frequency === 'QUARTERLY'
      ? 3
      : frequency === 'SEMIANNUAL'
        ? 6
        : frequency === 'CUSTOM'
          ? configuredInterval
          : 1;
  if (differenceInCalendarMonths(date, start) % interval !== 0) return false;
  if (
    values.recurVisitPatterns.some(
      (pattern) => pattern.kind === 'MONTH_DAY' || pattern.kind === 'MONTH_WEEKDAY',
    )
  ) {
    return matchesVisitPattern(date, values);
  }
  if (frequency === 'CUSTOM' && values.recurCustomDays.length > 0) {
    return values.recurCustomDays.includes(date.getDay());
  }
  return matchesMonthlySelector(date, start, values);
}

/**
 * Returns the next date the recurring generator can target from `today`.
 * Tenant holidays and closed weekdays are still enforced by the backend when
 * each visit is materialized; this mirrors the recurrence pattern itself.
 */
export function findNextRecurringOccurrenceDate(
  values: RecurringAvailabilityValues,
  todayDate: string,
  blackoutDates: string[] = [],
): string | null {
  const start = dateStringToDate(values.recurStartDate);
  const today = dateStringToDate(todayDate);
  if (!start || !today) return null;

  const end = values.recurEndType === 'ON_DATE' ? dateStringToDate(values.recurEndOnDate) : null;
  let candidate = start > today ? start : today;
  const blackoutSet = new Set(blackoutDates);

  for (let offset = 0; offset < 3660; offset += 1) {
    if (end && candidate > end) return null;
    const dateKey = dateToDateString(candidate);
    if (
      matchesRecurrence(candidate, start, values) &&
      !blackoutSet.has(dateKey) &&
      isWithinSeason(candidate, values.recurSeasonStart, values.recurSeasonEnd)
    ) {
      return dateKey;
    }
    candidate = addDays(candidate, 1);
  }

  return null;
}
